import { axiosForCloudStorageUrls } from 'utils/axiosInstance';
import {
  setUploadingProgressContext,
  setUploadedBytesContext,
} from 'context/NewIssueContext';
import logger from 'utils/logger';

const log = logger.createLogger('S3FileUpload');

export const MB = 1024 * 1024;
export const GB = 1024 * MB;
//! Reducing file chunk size to 20MB for China region
export const FILE_CHUNK_SIZE = 20 * MB;

// For retries
export const RETRY_COUNT = 5;
export const RETRY_DELAY = 1000; // 1 second

export const uploadFile = async ({
  file,
  presignedS3Url,
  batchTotalBytes,
  missionId,
  controllerUploadMissions,
  dispatchUploadMissions,
  axiosUploadController,
}) => {
  // Add this safety check at the beginning of the function
  if (!controllerUploadMissions?.missions?.[missionId]) {
    console.warn(
      `Mission ${missionId} not found in context, initializing with defaults`
    );
    return;
  }

  // Check if this is a multipart upload (presignedS3Url is an object with multiple URLs)
  const isMultipart =
    typeof presignedS3Url === 'object' &&
    presignedS3Url !== null &&
    !Array.isArray(presignedS3Url);

  if (isMultipart) {
    // Multipart upload logic
    const keys = Object.keys(presignedS3Url);
    const resParts = [];

    for (const indexStr of keys) {
      const uploadedBytesBase =
        controllerUploadMissions.missions[missionId].uploadedBytes;
      const index = parseInt(indexStr);
      const start = index * FILE_CHUNK_SIZE;
      const end = (index + 1) * FILE_CHUNK_SIZE;
      const blob =
        index < keys.length ? file.slice(start, end) : file.slice(start);

      const resPart = await uploadFileToS3({
        fileOrBlob: blob,
        presignedS3Url: presignedS3Url[index],
        batchTotalBytes,
        missionId,
        uploadedBytesBase,
        dispatchUploadMissions,
        axiosUploadController,
        gapThreshold: 0.25,
      });

      resParts.push(resPart);
    }

    return resParts.map((part, index) => ({
      ETag: part.headers.etag,
      PartNumber: index + 1,
    }));
  } else {
    const uploadedBytesBase =
      controllerUploadMissions.missions[missionId].uploadedBytes;

    await uploadFileToS3({
      fileOrBlob: file,
      presignedS3Url,
      batchTotalBytes,
      missionId,
      uploadedBytesBase,
      dispatchUploadMissions,
      axiosUploadController,
      gapThreshold: 0.9,
    });
  }

  return null; // Single file upload doesn't return parts
};

const uploadFileToS3 = async ({
  fileOrBlob,
  presignedS3Url,
  batchTotalBytes,
  missionId,
  uploadedBytesBase,
  dispatchUploadMissions,
  axiosUploadController,
  gapThreshold = 0.9,
  maxRetries = RETRY_COUNT,
  retryDelay = RETRY_DELAY,
}) => {
  let retryCount = 0;

  if (!fileOrBlob) {
    log.error('fileOrBlob is not defined');
    throw new Error('fileOrBlob is not defined');
  }

  while (retryCount <= maxRetries) {
    try {
      return await axiosForCloudStorageUrls({
        method: 'PUT',
        url: presignedS3Url,
        data: fileOrBlob,
        signal: axiosUploadController.signal,
        onUploadProgress: (data) => {
          // TODO Need to improve this so it shows the time correctly
          let maxProgress = 0;
          const gap = data.total - data.loaded;
          if (gap > 0 && Math.random() > gapThreshold) return;
          const newUploadedBytes = uploadedBytesBase + data.loaded;
          const uploadingProgress = Math.round(
            100 * (newUploadedBytes / batchTotalBytes)
          );

          if (uploadingProgress >= maxProgress) {
            maxProgress = uploadingProgress;

            setUploadedBytesContext(dispatchUploadMissions, {
              missionId,
              value: newUploadedBytes,
            });
            setUploadingProgressContext(dispatchUploadMissions, {
              missionId,
              value: uploadingProgress,
            });
          }
        },
      });
    } catch (error) {
      retryCount++;

      // Don't retry if it's a cancellation error
      if (
        error.name === 'CanceledError' ||
        (error.signal && error.signal.aborted)
      ) {
        throw error;
      }

      // If we've exhausted retries, throw the error
      if (retryCount > maxRetries) {
        throw error;
      }

      // Wait before retrying (with exponential backoff)
      const delay = retryDelay * Math.pow(2, retryCount - 1);
      await new Promise((resolve) => setTimeout(resolve, delay));

      console.warn(
        `Retrying uploadFileToS3 (attempt ${retryCount}/${maxRetries})`
      );
    }
  }
};
